import { useMemo } from 'react';
import { Info } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@design-system';
import {
  useFundingSources,
  usePartnerLockedFundingSources,
} from '@/features/guided-financial-planning/hooks';
import { useSession } from '@/features/guided-financial-planning/context';
import { type AssetTypeId } from '@/features/funding-sources';
import { FundingSourceCard } from './funding-source-card';
import { AddAccountButton } from './funding-source-dialog';
import { GUIDED_POLICY_FUNDING_ASSUMPTION } from '@/features/guided-financial-planning/funding-disclaimer';

interface PersistedSelection {
  action: string;
  originalValue: number;
  customClientPercent?: number;
}

function getClientEffectiveBalance(selection: PersistedSelection): number {
  const { action, originalValue, customClientPercent } = selection;
  if (action === 'mirror') return originalValue;
  if (action === 'split-50-50') return Math.round(originalValue * 0.5);
  if (action === 'split-custom' && customClientPercent != null) {
    return Math.round(originalValue * (customClientPercent / 100));
  }
  // Legacy split actions (e.g. 'split-70-30') — parse from action name
  if (action.startsWith('split-') && customClientPercent != null) {
    return Math.round(originalValue * (customClientPercent / 100));
  }
  return originalValue;
}

interface FundingSourcesListProps {
  onEditSource?: (selfFundingSourceId: string, selfFundingSource: SelfFundingSource) => void;
  onAddSource?: (assetTypeId: AssetTypeId, label: string) => void;
  disableAdd?: boolean;
  showPolicyDeductionAssumption?: boolean;
}

export function FundingSourcesList({
  onEditSource,
  onAddSource,
  disableAdd,
  showPolicyDeductionAssumption = false,
}: FundingSourcesListProps) {
  const fundingSources = useFundingSources();
  const selfFunding = fundingSources?.selfFunding ?? {};
  const { partnerSharedFundingSourceIds, partnerFirstName } = usePartnerLockedFundingSources();
  const { session } = useSession();

  const partnerAllocations = useMemo(
    () =>
      (session?.stepData?.partnerAllocationSelections ?? {}) as Record<string, PersistedSelection>,
    [session?.stepData?.partnerAllocationSelections],
  );

  // Filter to only show sources with existingAssetsValue (no empty shells)
  const validSources = useMemo(() => {
    return Object.entries(selfFunding).filter(([, source]) => {
      return source.existingAssetsValue != null && source.existingAssetsValue > 0;
    });
  }, [selfFunding]);

  return (
    <div className="space-y-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 text-gray-900 hover:text-gray-700"
          >
            <span className="text-base font-semibold">Select funding accounts:</span>
            <Info className="size-4 text-gray-400" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs space-y-2 p-3"
        >
          <p className="font-medium">Simplified estimate</p>
          <p>We're using your current account balances to estimate available funds.</p>
          <p className="text-gray-300">
            Coming soon: monthly contributions, investment growth, and tax considerations.
          </p>
        </TooltipContent>
      </Tooltip>
      {showPolicyDeductionAssumption && (
        <Alert variant="info">
          <Info aria-hidden="true" />
          <AlertDescription>{GUIDED_POLICY_FUNDING_ASSUMPTION}</AlertDescription>
        </Alert>
      )}
      {validSources.length === 0 && (
        <div className="py-4 text-center">
          <p className="text-sm text-gray-500">No funding sources available.</p>
        </div>
      )}
      {validSources.map(([selfFundingSourceId, source]) => {
        const isPartnerShared = partnerSharedFundingSourceIds.has(selfFundingSourceId);
        const allocationSelection = partnerAllocations[selfFundingSourceId];
        const effectiveBalance =
          allocationSelection && allocationSelection.action !== 'keep'
            ? getClientEffectiveBalance(allocationSelection)
            : null;
        return (
          <FundingSourceCard
            key={selfFundingSourceId}
            selfFundingSourceId={selfFundingSourceId}
            selfFundingSource={source as SelfFundingSource}
            onEdit={isPartnerShared ? undefined : onEditSource}
            sharedByPartnerName={isPartnerShared ? (partnerFirstName ?? 'Partner') : null}
            effectiveBalance={effectiveBalance}
          />
        );
      })}
      {onAddSource && (
        <AddAccountButton
          onSelectAssetType={onAddSource}
          disabled={disableAdd}
        />
      )}
    </div>
  );
}
